import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { PROVISIONAL_OFFER_TERMS_AND_CONDITIONS } from '../../../../constants/heapEvents';
import { track } from '../../../../lib/heapHelpers';
import { scrollToTermsAndConditions } from '../../../../lib/provisionalOfferHelper';

import './index.scss';

const OfferTermsAndConditions = ({
  alreadyAccepted,
  onAcceptTermsAndConditions,
  showReminder,
  applicationId,
  customerGuid,
  offerIssuedDuringPromoPeriod,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleList = () => setIsExpanded(!isExpanded);

  const [accepted, setAccepted] = useState(alreadyAccepted);
  const onCheckboxChange = e => {
    const accepted = e.target.checked;
    setAccepted(accepted);
    onAcceptTermsAndConditions(accepted);
    track(PROVISIONAL_OFFER_TERMS_AND_CONDITIONS, {
      'Application GUID': applicationId,
      'Customer GUID': customerGuid,
      'Terms and conditions accepted': accepted,
    });
  };

  useEffect(() => {
    if (!showReminder && !isExpanded) return;
    scrollToTermsAndConditions();
  }, [showReminder, isExpanded]);

  return (
    <div className="terms-and-conditions">
      <div className="provisional-offer__section">
        <div
          className={`terms-and-conditions__title ${showReminder ? 'highlight' : ''}`}
          data-testid="toc-title"
        >
          <div className="terms-and-conditions__title__checkbox">
            <input
              type="checkbox"
              checked={accepted}
              readOnly={alreadyAccepted}
              disabled={alreadyAccepted}
              onChange={onCheckboxChange}
              data-testid="toc-checkbox"
            />
            <span className="outline"></span>
          </div>
          <div className="terms-and-conditions__title__text">
            <h6>
              I agree to the <span onClick={toggleList}>Terms and Conditions</span>
            </h6>
          </div>
          <div
            onClick={toggleList}
            className={`terms-and-conditions__title__expand-icon ${
              isExpanded ? 'chevron-up' : 'chevron-down'
            }`}
          ></div>
          {showReminder && (
            <span className="terms-and-conditions__title__reminder h6">
              Please agree to proceed
            </span>
          )}
        </div>
        <div
          className={`terms-and-conditions__list ${isExpanded ? 'list-open' : ''}`}
          data-testid="toc-list"
        >
          <ul>
            {!alreadyAccepted && (
              <li>
                By clicking "Accept" you agree to continue your application to the next phase where
                we'll need to verify your supporting documentation before you can be matched to
                funding. If you’ve received multiple provisional offers from us, we’ll withdraw all
                others. However, if you change your mind, we can reinstate one of the withdrawn
                applications.
              </li>
            )}
            <li>
              This provisional offer is conditional on your application meeting our funding
              requirements, and that all the information you gave us is complete and correct. You
              may not incur additional debt during your application process or your study period. We
              have the right to request additional credit reports prior to finalising your loan or
              paying the funds. If we suspect fraud, we may pass your details on to fraud prevention
              agencies or ask them for more information.
            </li>
            <li>
              If we’ve pre-approved funding for additional period(s), it is conditional on you
              applying for these additional loans and meeting our eligibility and funding criteria.
              If you take on other debt or miss any payments on your other credit, we may decline
              your application(s).
            </li>
            <li>
              The repayment terms of{' '}
              {alreadyAccepted ? ' the accepted loan offer ' : ' each quoted option '}
              assume a fixed monthly payment amount, no missed payments and a constant interest rate
              for the life of the loan. The variable part of the interest rate varies due to market
              changes. The estimated APR is calculated using the definition described by the&nbsp;
              <a href="https://www.handbook.fca.org.uk/" target="_blank" rel="noreferrer">
                FCA handbook (UK)
              </a>
              .
            </li>
            <li>
              Your loan application is subject to contract and is only binding when you and Prodigy
              Finance have signed the loan agreement. You can withdraw your application without
              penalty at any time before you sign your loan agreement.
            </li>
            <li>
              The predicted future curve for the variable portion of the interest rate is not
              guaranteed and is only provided as an illustration of the potential rate change over
              time.
            </li>
          </ul>
          {offerIssuedDuringPromoPeriod && (
            <>
              Terms and conditions for limited rate discount promotion
              <ul data-testid="toc-list-promo">
                <li>
                  The promotion is valid for eligible customers submitting loan applications from 23
                  April - 21 May 2024 inclusive. The fixed portion of your interest rate stated in
                  your offer includes the discount of up to 3% and has been determined by Prodigy
                  Finance based on your application.
                </li>
                <li>
                  If you have multiple offers from us and you accept another offer, this offer will
                  be withdrawn and cannot be reinstated after the promotional period has ended.
                </li>
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

OfferTermsAndConditions.propTypes = {
  alreadyAccepted: PropTypes.bool.isRequired,
  onAcceptTermsAndConditions: PropTypes.func.isRequired,
  showReminder: PropTypes.bool.isRequired,
  applicationId: PropTypes.string.isRequired,
  customerGuid: PropTypes.string.isRequired,
  offerIssuedDuringPromoPeriod: PropTypes.bool.isRequired,
};

export default OfferTermsAndConditions;
